/**
 * HVAC PDF Report Generator
 * Creates professional multi-page PDF reports for HVAC data
 */

import { jsPDF } from 'jspdf';

// Paper dimensions in points (72 points = 1 inch)
const LETTER_WIDTH = 612;
const LETTER_HEIGHT = 792;
const MARGIN = 50;
const CONTENT_WIDTH = LETTER_WIDTH - (MARGIN * 2);

// Colors
const COLORS = {
  primary: [0, 100, 180],      // Blue
  secondary: [80, 80, 80],     // Dark gray
  accent: [0, 200, 255],       // Cyan
  lightGray: [240, 240, 240],
  tableHeader: [30, 60, 90],
  tableRow: [250, 250, 250],
  supply: [230, 81, 0],        // Orange
  return: [21, 101, 192],      // Blue
};

/**
 * Generate HVAC PDF Report
 */
export async function generateHVACReport(activeFloor, projectInfo, canvasRef) {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
  });

  const rooms = activeFloor?.rooms || [];
  const roomData = activeFloor?.roomData || [];
  const equipment = activeFloor?.equipment || [];
  const ducts = activeFloor?.ducts || [];
  const vents = activeFloor?.vents || [];

  // Page 1: Cover Page
  drawCoverPage(pdf, projectInfo);

  // Page 2: Floor Plan with HVAC
  pdf.addPage();
  await drawFloorPlanPage(pdf, canvasRef);

  // Page 3: Room Data Table
  pdf.addPage();
  drawRoomDataPage(pdf, rooms, roomData);

  // Page 4: Equipment Schedule
  pdf.addPage();
  drawEquipmentPage(pdf, equipment, vents, ducts);

  // Page 5: Notes
  pdf.addPage();
  drawNotesPage(pdf, roomData, projectInfo);

  return pdf;
}

/**
 * Download the HVAC PDF Report
 */
export async function downloadHVACReport(activeFloor, projectInfo, canvasRef) {
  const pdf = await generateHVACReport(activeFloor, projectInfo, canvasRef);
  const filename = `hvac_report_${(projectInfo?.projectName || 'project').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(filename);
}

/**
 * Draw Cover Page
 */
function drawCoverPage(pdf, projectInfo) {
  const centerX = LETTER_WIDTH / 2;

  // Header bar
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 0, LETTER_WIDTH, 120, 'F');

  // Title
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.text('HVAC SYSTEM REPORT', centerX, 70, { align: 'center' });

  // Subtitle
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Manual J Data & Equipment Schedule', centerX, 95, { align: 'center' });

  // Project Info Box
  const boxY = 180;
  const boxHeight = 200;
  pdf.setFillColor(...COLORS.lightGray);
  pdf.roundedRect(MARGIN, boxY, CONTENT_WIDTH, boxHeight, 8, 8, 'F');

  pdf.setTextColor(...COLORS.secondary);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PROJECT INFORMATION', MARGIN + 20, boxY + 30);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(14);
  const infoY = boxY + 60;
  const lineHeight = 28;

  drawInfoRow(pdf, 'Project Name:', projectInfo?.projectName || 'Untitled Project', MARGIN + 20, infoY);
  drawInfoRow(pdf, 'Client:', projectInfo?.clientName || '-', MARGIN + 20, infoY + lineHeight);
  drawInfoRow(pdf, 'Address:', projectInfo?.address || '-', MARGIN + 20, infoY + lineHeight * 2);
  drawInfoRow(pdf, 'Prepared By:', projectInfo?.drawnBy || '-', MARGIN + 20, infoY + lineHeight * 3);
  drawInfoRow(pdf, 'Date:', new Date().toLocaleDateString(), MARGIN + 20, infoY + lineHeight * 4);

  // Contractor Logo Area (placeholder)
  const logoBoxY = 440;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(1);
  pdf.roundedRect(MARGIN, logoBoxY, CONTENT_WIDTH, 100, 8, 8, 'S');
  pdf.setFontSize(10);
  pdf.setTextColor(150, 150, 150);
  pdf.text('Company Logo Area', centerX, logoBoxY + 55, { align: 'center' });

  // Footer
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, LETTER_HEIGHT - 60, LETTER_WIDTH, 60, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(10);
  pdf.text('Generated by FloorPlan Pro', centerX, LETTER_HEIGHT - 35, { align: 'center' });
  pdf.setFontSize(8);
  pdf.text('www.floorplanpro.com', centerX, LETTER_HEIGHT - 20, { align: 'center' });
}

/**
 * Draw info row with label and value
 */
function drawInfoRow(pdf, label, value, x, y) {
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.secondary);
  pdf.text(label, x, y);
  pdf.setFont('helvetica', 'normal');
  pdf.text(String(value), x + 100, y);
}

/**
 * Draw Floor Plan Page
 */
async function drawFloorPlanPage(pdf, canvasRef) {
  // Header
  drawPageHeader(pdf, 'Floor Plan with HVAC Layout', 2);

  // Try to capture canvas image
  if (canvasRef?.current) {
    try {
      const canvas = canvasRef.current;
      const imgData = canvas.toDataURL('image/png', 1.0);

      // Calculate dimensions to fit page
      const maxWidth = CONTENT_WIDTH;
      const maxHeight = 500;
      const canvasAspect = canvas.width / canvas.height;

      let imgWidth, imgHeight;
      if (canvasAspect > maxWidth / maxHeight) {
        imgWidth = maxWidth;
        imgHeight = maxWidth / canvasAspect;
      } else {
        imgHeight = maxHeight;
        imgWidth = maxHeight * canvasAspect;
      }

      const imgX = (LETTER_WIDTH - imgWidth) / 2;
      pdf.addImage(imgData, 'PNG', imgX, 100, imgWidth, imgHeight);
    } catch (err) {
      console.error('Failed to capture canvas:', err);
      pdf.setTextColor(150, 150, 150);
      pdf.setFontSize(14);
      pdf.text('Floor plan image not available', LETTER_WIDTH / 2, 300, { align: 'center' });
    }
  } else {
    pdf.setTextColor(150, 150, 150);
    pdf.setFontSize(14);
    pdf.text('Floor plan image not available', LETTER_WIDTH / 2, 300, { align: 'center' });
  }

  // Legend
  const legendY = 620;
  pdf.setFillColor(...COLORS.lightGray);
  pdf.roundedRect(MARGIN, legendY, CONTENT_WIDTH, 80, 5, 5, 'F');

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.secondary);
  pdf.text('LEGEND', MARGIN + 15, legendY + 20);

  // Legend items
  const legendItems = [
    { color: COLORS.supply, label: 'Supply Duct / Vent' },
    { color: COLORS.return, label: 'Return Duct / Grille' },
    { color: [102, 187, 106], label: 'Equipment' },
    { color: [171, 71, 188], label: 'Fittings' },
  ];

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  legendItems.forEach((item, i) => {
    const x = MARGIN + 15 + (i * 130);
    pdf.setFillColor(...item.color);
    pdf.rect(x, legendY + 35, 12, 12, 'F');
    pdf.setTextColor(...COLORS.secondary);
    pdf.text(item.label, x + 18, legendY + 45);
  });

  // Scale note
  pdf.setFontSize(8);
  pdf.setTextColor(120, 120, 120);
  pdf.text('Scale: As shown on floor plan', MARGIN + 15, legendY + 70);

  drawPageFooter(pdf, 2);
}

/**
 * Draw Room Data Page
 */
function drawRoomDataPage(pdf, rooms, roomData) {
  drawPageHeader(pdf, 'Room Data Schedule', 3);

  // Table headers
  const headers = ['Room', 'SqFt', 'Ceiling', 'Windows', 'Ext. Walls', 'Floor'];
  const colWidths = [120, 60, 100, 80, 80, 72];
  const startY = 100;
  const rowHeight = 24;

  // Draw header row
  let x = MARGIN;
  pdf.setFillColor(...COLORS.tableHeader);
  pdf.rect(MARGIN, startY, CONTENT_WIDTH, rowHeight, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  headers.forEach((header, i) => {
    pdf.text(header, x + 5, startY + 16);
    x += colWidths[i];
  });

  // Draw data rows
  let y = startY + rowHeight;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);

  rooms.forEach((room, index) => {
    const data = roomData?.find(rd => rd.roomId === room.id) || {};

    // Alternate row colors
    if (index % 2 === 0) {
      pdf.setFillColor(...COLORS.tableRow);
      pdf.rect(MARGIN, y, CONTENT_WIDTH, rowHeight, 'F');
    }

    pdf.setTextColor(...COLORS.secondary);
    x = MARGIN;

    // Room name
    pdf.text(truncateText(room.name || 'Room ' + (index + 1), 18), x + 5, y + 16);
    x += colWidths[0];

    // Square feet
    pdf.text(String(data.floorArea || room.area || 0), x + 5, y + 16);
    x += colWidths[1];

    // Ceiling
    const ceilingText = `${data.ceilingHeight || 8}' ${formatCeilingType(data.ceilingType)}`;
    pdf.text(truncateText(ceilingText, 15), x + 5, y + 16);
    x += colWidths[2];

    // Windows
    const windowCount = (data.windows || []).length;
    const windowArea = (data.windows || []).reduce((sum, w) => sum + ((w.width || 36) * (w.height || 48)) / 144, 0);
    pdf.text(`${windowCount} (${Math.round(windowArea)} sf)`, x + 5, y + 16);
    x += colWidths[3];

    // Exterior walls
    const wallCount = (data.exteriorWalls || []).length;
    const wallLength = (data.exteriorWalls || []).reduce((sum, w) => sum + (w.length || 0), 0);
    pdf.text(`${wallCount} (${Math.round(wallLength)}')`, x + 5, y + 16);
    x += colWidths[4];

    // Floor type
    pdf.text(formatFloorType(data.floorType), x + 5, y + 16);

    y += rowHeight;

    // Check for page break
    if (y > LETTER_HEIGHT - 100) {
      pdf.addPage();
      drawPageHeader(pdf, 'Room Data Schedule (continued)', 3);
      y = 100;
    }
  });

  // Summary row
  y += 10;
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(MARGIN, y, CONTENT_WIDTH, rowHeight, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');

  const totalSqft = rooms.reduce((sum, room) => {
    const data = roomData?.find(rd => rd.roomId === room.id);
    return sum + (data?.floorArea || room.area || 0);
  }, 0);

  pdf.text(`TOTAL: ${rooms.length} Rooms`, MARGIN + 5, y + 16);
  pdf.text(`${totalSqft} sqft`, MARGIN + colWidths[0] + 5, y + 16);

  drawPageFooter(pdf, 3);
}

/**
 * Draw Equipment Page
 */
function drawEquipmentPage(pdf, equipment, vents, ducts) {
  drawPageHeader(pdf, 'Equipment & Vent Schedule', 4);

  let y = 100;

  // Equipment Table
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.primary);
  pdf.text('EQUIPMENT', MARGIN, y);
  y += 20;

  if (equipment.length > 0) {
    // Equipment headers
    const eqHeaders = ['Type', 'Size', 'Location'];
    const eqColWidths = [200, 150, 162];
    drawTableHeader(pdf, eqHeaders, eqColWidths, y);
    y += 24;

    equipment.forEach((eq, index) => {
      if (index % 2 === 0) {
        pdf.setFillColor(...COLORS.tableRow);
        pdf.rect(MARGIN, y, CONTENT_WIDTH, 22, 'F');
      }

      pdf.setTextColor(...COLORS.secondary);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);

      let x = MARGIN;
      pdf.text(formatEquipmentType(eq.type), x + 5, y + 15);
      x += eqColWidths[0];
      pdf.text(eq.size || 'Standard', x + 5, y + 15);
      x += eqColWidths[1];
      pdf.text(`(${Math.round(eq.x / 40)}', ${Math.round(eq.y / 40)}')`, x + 5, y + 15);

      y += 22;
    });
  } else {
    pdf.setTextColor(150, 150, 150);
    pdf.setFontSize(10);
    pdf.text('No equipment specified', MARGIN + 20, y + 10);
    y += 30;
  }

  y += 30;

  // Vents Table
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.primary);
  pdf.text('SUPPLY & RETURN VENTS', MARGIN, y);
  y += 20;

  if (vents.length > 0) {
    const ventHeaders = ['Type', 'Size', 'CFM', 'Location'];
    const ventColWidths = [150, 100, 80, 182];
    drawTableHeader(pdf, ventHeaders, ventColWidths, y);
    y += 24;

    vents.forEach((vent, index) => {
      if (index % 2 === 0) {
        pdf.setFillColor(...COLORS.tableRow);
        pdf.rect(MARGIN, y, CONTENT_WIDTH, 22, 'F');
      }

      // Color code by type
      const isSupply = vent.type?.startsWith('supply');
      pdf.setFillColor(...(isSupply ? COLORS.supply : COLORS.return));
      pdf.rect(MARGIN + 2, y + 6, 8, 10, 'F');

      pdf.setTextColor(...COLORS.secondary);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);

      let x = MARGIN;
      pdf.text(formatVentType(vent.type), x + 15, y + 15);
      x += ventColWidths[0];
      pdf.text(vent.size || '6x12', x + 5, y + 15);
      x += ventColWidths[1];
      pdf.text(String(vent.cfm || '-'), x + 5, y + 15);
      x += ventColWidths[2];
      pdf.text(`(${Math.round(vent.x / 40)}', ${Math.round(vent.y / 40)}')`, x + 5, y + 15);

      y += 22;
    });

    // Vent summary
    y += 10;
    const supplyVents = vents.filter(v => v.type?.startsWith('supply'));
    const returnVents = vents.filter(v => v.type?.startsWith('return'));
    const totalSupplyCfm = supplyVents.reduce((sum, v) => sum + (v.cfm || 0), 0);
    const totalReturnCfm = returnVents.reduce((sum, v) => sum + (v.cfm || 0), 0);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text(`Supply: ${supplyVents.length} vents, ${totalSupplyCfm} CFM total`, MARGIN, y + 15);
    pdf.text(`Return: ${returnVents.length} grilles, ${totalReturnCfm} CFM total`, MARGIN + 250, y + 15);
  } else {
    pdf.setTextColor(150, 150, 150);
    pdf.setFontSize(10);
    pdf.text('No vents specified', MARGIN + 20, y + 10);
  }

  // Ductwork summary
  y += 50;
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.primary);
  pdf.text('DUCTWORK SUMMARY', MARGIN, y);
  y += 25;

  if (ducts.length > 0) {
    const calcLength = (duct) => {
      if (!duct.points || duct.points.length < 2) return 0;
      let len = 0;
      for (let i = 0; i < duct.points.length - 1; i++) {
        const dx = duct.points[i + 1].x - duct.points[i].x;
        const dy = duct.points[i + 1].y - duct.points[i].y;
        len += Math.sqrt(dx * dx + dy * dy) / 40;
      }
      return len;
    };

    const supplyDucts = ducts.filter(d => d.type === 'supply');
    const returnDucts = ducts.filter(d => d.type === 'return');
    const supplyLength = supplyDucts.reduce((sum, d) => sum + calcLength(d), 0);
    const returnLength = returnDucts.reduce((sum, d) => sum + calcLength(d), 0);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.secondary);
    pdf.text(`Supply Ductwork: ${supplyDucts.length} runs, ${Math.round(supplyLength)} linear feet`, MARGIN + 20, y);
    pdf.text(`Return Ductwork: ${returnDucts.length} runs, ${Math.round(returnLength)} linear feet`, MARGIN + 20, y + 18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Total Ductwork: ${Math.round(supplyLength + returnLength)} linear feet`, MARGIN + 20, y + 40);
  } else {
    pdf.setTextColor(150, 150, 150);
    pdf.setFontSize(10);
    pdf.text('No ductwork specified', MARGIN + 20, y);
  }

  drawPageFooter(pdf, 4);
}

/**
 * Draw Notes Page
 */
function drawNotesPage(pdf, roomData, projectInfo) {
  drawPageHeader(pdf, 'Notes & Additional Information', 5);

  let y = 100;

  // Collect all notes from rooms
  const allNotes = (roomData || [])
    .filter(rd => rd.notes && rd.notes.trim())
    .map(rd => ({
      room: rd.name || 'Room',
      note: rd.notes,
    }));

  if (allNotes.length > 0) {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.primary);
    pdf.text('ROOM NOTES', MARGIN, y);
    y += 25;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.secondary);

    allNotes.forEach(({ room, note }) => {
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${room}:`, MARGIN + 10, y);
      pdf.setFont('helvetica', 'normal');

      // Word wrap the note
      const lines = pdf.splitTextToSize(note, CONTENT_WIDTH - 30);
      lines.forEach((line, i) => {
        pdf.text(line, MARGIN + 20, y + 15 + (i * 14));
      });
      y += 15 + (lines.length * 14) + 10;
    });
  }

  y += 30;

  // Additional notes section (blank lines for handwritten notes)
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.primary);
  pdf.text('ADDITIONAL NOTES', MARGIN, y);
  y += 25;

  // Draw lined area for notes
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.5);
  for (let i = 0; i < 15; i++) {
    pdf.line(MARGIN, y + (i * 25), LETTER_WIDTH - MARGIN, y + (i * 25));
  }

  drawPageFooter(pdf, 5);
}

/**
 * Draw page header
 */
function drawPageHeader(pdf, title, pageNum) {
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 0, LETTER_WIDTH, 50, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, MARGIN, 32);

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Page ${pageNum}`, LETTER_WIDTH - MARGIN - 40, 32);
}

/**
 * Draw page footer
 */
function drawPageFooter(pdf, pageNum) {
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, LETTER_HEIGHT - 40, LETTER_WIDTH - MARGIN, LETTER_HEIGHT - 40);

  pdf.setFontSize(8);
  pdf.setTextColor(120, 120, 120);
  pdf.text('Generated by FloorPlan Pro', MARGIN, LETTER_HEIGHT - 25);
  pdf.text(new Date().toLocaleString(), LETTER_WIDTH - MARGIN - 100, LETTER_HEIGHT - 25);
}

/**
 * Draw table header row
 */
function drawTableHeader(pdf, headers, colWidths, y) {
  pdf.setFillColor(...COLORS.tableHeader);
  pdf.rect(MARGIN, y, CONTENT_WIDTH, 22, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');

  let x = MARGIN;
  headers.forEach((header, i) => {
    pdf.text(header, x + 5, y + 15);
    x += colWidths[i];
  });
}

/**
 * Utility functions
 */
function truncateText(text, maxLen) {
  if (!text) return '';
  return text.length > maxLen ? text.substring(0, maxLen - 2) + '..' : text;
}

function formatCeilingType(type) {
  const types = {
    'below-vented-attic': 'Attic',
    'below-unvented-attic': 'Unvent Attic',
    'cathedral': 'Cathedral',
    'flat-roof': 'Flat',
    'below-conditioned': 'Cond. Space',
  };
  return types[type] || type || 'Attic';
}

function formatFloorType(type) {
  const types = {
    'slab': 'Slab',
    'crawl-vented': 'Crawl (V)',
    'crawl-unvented': 'Crawl (UV)',
    'basement-conditioned': 'Bsmt (C)',
    'basement-unconditioned': 'Bsmt (UC)',
    'over-conditioned': 'Over Cond.',
  };
  return types[type] || type || 'Slab';
}

function formatEquipmentType(type) {
  const types = {
    'furnace-gas': 'Gas Furnace',
    'furnace-electric': 'Electric Furnace',
    'heat-pump': 'Heat Pump',
    'ac-condenser': 'A/C Condenser',
    'air-handler': 'Air Handler',
    'mini-split-indoor': 'Mini Split (Indoor)',
    'mini-split-outdoor': 'Mini Split (Outdoor)',
    'boiler': 'Boiler',
    'water-heater': 'Water Heater',
    'thermostat': 'Thermostat',
  };
  return types[type] || type || 'Unknown';
}

function formatVentType(type) {
  const types = {
    'supply-floor': 'Floor Register',
    'supply-ceiling': 'Ceiling Diffuser',
    'supply-wall': 'Wall Register',
    'supply-linear': 'Linear Diffuser',
    'return-standard': 'Return Grille',
    'return-filter': 'Filter Grille',
    'return-jumper': 'Jump Duct',
  };
  return types[type] || type || 'Unknown';
}
